/** @file dock-manager/BookingPeek.jsx — tooltip preview for a booking card. */
import { ShieldAlert, Truck } from "lucide-react";
import { outstandingFromSnapshot } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { STATUS_BADGE_CLASS } from "@/lib/constants";
import { fmtRange, fmtFull } from "./formatters";
import { useFormVisibility } from "@/lib/formVisibility";

export default function BookingPeek({ booking }) {
  const vis = useFormVisibility(booking.client_id);
  const show = (k) => booking[k] && vis.fieldVisible(k);
  const customer =
    (booking.direction === "outbound"
      ? booking.receiver_company || booking.receiver_name
      : booking.sender_company || booking.sender_name) || "—";
  const pending = outstandingFromSnapshot(booking.declarations);
  return (
    <div className="space-y-1 text-xs">
      <div className="font-semibold">
        {booking.direction === "inbound" ? "IN" : "OUT"} · {customer}
      </div>
      <div className="text-slate-200">
        {fmtFull(booking.scheduled_start)} → {fmtFull(booking.scheduled_end)}
      </div>
      {(booking.driver?.first_name || booking.driver?.last_name || booking.driver_first_name) && (
        <div className="text-slate-100">
          Driver: {[booking.driver?.first_name || booking.driver_first_name, booking.driver?.last_name || booking.driver_last_name].filter(Boolean).join(" ")}
        </div>
      )}
      {booking.vehicle_regos?.length > 0 && (
        <div className="flex items-center gap-1.5 text-slate-100">
          <Truck className="h-3 w-3" /> {booking.vehicle_regos.join(", ")}
        </div>
      )}
      {show("sender_company") && show("receiver_company") && (
        <div className="text-slate-100">
          {booking.sender_company} <span className="opacity-50">→</span> {booking.receiver_company}
        </div>
      )}
      {show("po_number") && (
        <div className="text-slate-100">{vis.fieldLabel("po_number", "PO")}: {booking.po_number}</div>
      )}
      {show("manifest_number") && (
        <div className="text-slate-100">{vis.fieldLabel("manifest_number", "Manifest")}: {booking.manifest_number}</div>
      )}
      {pending.length > 0 && (
        <div className="flex items-start gap-1.5 text-amber-300" data-testid="peek-declarations-pending">
          <ShieldAlert className="h-3 w-3 mt-0.5" /> To confirm at arrival: {pending.map((d) => d.short_label || d.statement).join(", ")}
        </div>
      )}
      <div className="border-t border-slate-700 mt-1 pt-1 text-slate-400 text-[10px] font-mono space-y-0.5">
        {show("internal_ref") && <div>{vis.fieldLabel("internal_ref", "Client ref")}: {booking.internal_ref}</div>}
        {show("external_ref") && <div>{vis.fieldLabel("external_ref", "Customer ref")}: {booking.external_ref}</div>}
        <div>{booking.reference_number}</div>
      </div>
    </div>
  );
}

